import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import SvgIcon from './SvgIcon';
import { IAnnouncement } from '../../RidePlayer/types';
import {
  ANNOUNCEMENT_TYPE_ALARM,
  ANNOUNCEMENT_TYPE_EVENT,
  ANNOUNCEMENT_TYPE_EVENT_BINARY,
  ANNOUNCEMENT_TYPE_EVENT_BLUECOINS,
  ANNOUNCEMENT_TYPE_EVENT_DOORS,
  isOnlinePanelOpenAtom,
  IVehicleAlarmItem,
} from '../../OnlinePanel/OnlinePanelState';
import { getUserId } from '../../../Services/UserService';
import IconService from '../../../Services/IconService';
import Config from '../../../../config';
import { IVehicleGdprDataAllowed } from '../../Vehicles/types';
import {
  isVehicleGdprDataAllowedAtom,
  vehicleWithDataSelector,
} from '../../../store/recoil/vehicles';
import { isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import {
  autofocusAtom,
  filteredVehiclesAtom,
  filterStringAtom,
  isRidePlayerOpenAtom,
  vehicleFocusAtom,
} from '../OnlineMapState';
import { isWaypointsOpenAtom } from '../Waypoints/WaypointState';
import {
  mapInfoWindowAdditionalAtom,
  mapInfoWindowAlarmsAtom,
  mapInfoWindowBluecoinsAtom,
  mapInfoWindowDoorsAtom,
  mapInfoWindowDriverNameAtom,
  mapInfoWindowEventsAtom,
  mapInfoWindowIdentificationAtom,
  mapInfoWindowLicenceNumberAtom,
  mapInfoWindowVehicleNameAtom,
} from '../ToolbarLayer/VehicleChipsControl/InfoWindowState';
import useAnalytics from '../../../hooks/useAnalytics';

const UMATCHED_MARKER_OPACITY = 0.3;
const MATCHED_MARKER_OPACITY = 1;
export const DOOR_EVENT_TYPE = 'door';
export const ADDITIONAL_EVENT_TYPE = 'binary';

interface IVehicleMarkerProps {
  vehicleId: number;
  lat: number; // eslint-disable-line react/no-unused-prop-types
  lng: number; // eslint-disable-line react/no-unused-prop-types
  zIndex: number;
}

export default function VehicleMarker(props: IVehicleMarkerProps): JSX.Element | null {
  const vehicle = useRecoilValue(vehicleWithDataSelector(props.vehicleId));
  const setVehicleFocus = useSetRecoilState<boolean>(vehicleFocusAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );
  const [isOnlinePanelOpen, setIsOnlinePanelOpen] = useRecoilState<number | false>(
    isOnlinePanelOpenAtom
  );
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);
  const filterString = useRecoilValue<string>(filterStringAtom);
  const filteredVehicles = useRecoilValue<number[]>(filteredVehiclesAtom);
  const identification = useRecoilValue(mapInfoWindowIdentificationAtom);
  const alarms = useRecoilValue(mapInfoWindowAlarmsAtom);
  const eventsSettings = useRecoilValue(mapInfoWindowEventsAtom);
  const doors = useRecoilValue(mapInfoWindowDoorsAtom);
  const licenceNumberSettings = useRecoilValue(mapInfoWindowLicenceNumberAtom);
  const driverNameSettings = useRecoilValue(mapInfoWindowDriverNameAtom);
  const vehicleName = useRecoilValue(mapInfoWindowVehicleNameAtom);
  const additional = useRecoilValue(mapInfoWindowAdditionalAtom);
  const bluecoins = useRecoilValue(mapInfoWindowBluecoinsAtom);
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const { trackEvent } = useAnalytics();

  if (!vehicle) {
    console.warn('Cannot render vehicle marker for vehicle: ', props.vehicleId);
    return null;
  }

  const getOpacity = () => {
    if (filterString.length === 0 && !isWaypointsOpen && !isBluecoinsOpen) {
      return MATCHED_MARKER_OPACITY;
    }
    if (
      filterString.length > 0 &&
      Object.values(filteredVehicles).includes(vehicle.id) &&
      !isWaypointsOpen &&
      !isBluecoinsOpen
    ) {
      return MATCHED_MARKER_OPACITY;
    }
    return UMATCHED_MARKER_OPACITY;
  };

  const handleOnClick = (): void => {
    if (getOpacity() === 1) {
      if (typeof isOnlinePanelOpen === 'number' && isOnlinePanelOpen === props.vehicleId) {
        setIsOnlinePanelOpen(false);
        trackEvent(['map', 'click', `Close online panel for vehicle ${props.vehicleId}`]);
      } else {
        trackEvent(['map', 'click', `Open online panel for vehicle ${props.vehicleId}`]);
        setIsOnlinePanelOpen(props.vehicleId);
        setVehicleFocus(true);
        setAutofocus(false);
      }
    }
  };

  const checkAlarmOnVehicle = (): boolean => {
    const vehicleOnlinePanelData = vehicle.onlinePanel;

    const userId = getUserId();

    let alarmsForUser: IVehicleAlarmItem[] = [];

    if (vehicleOnlinePanelData && vehicleOnlinePanelData.announcements && userId) {
      alarmsForUser = vehicleOnlinePanelData.announcements
        .filter(
          (item: IAnnouncement) =>
            item.type === ANNOUNCEMENT_TYPE_ALARM &&
            item.visibilityMap &&
            item.visibilityMap.includes(userId)
        )
        .map((item: IAnnouncement) => {
          const alarm: IVehicleAlarmItem = {
            id: item.id,
            name: item.title || '',
            users: item.visibilityMap || [],
          };
          return alarm;
        });
    }

    if (
      alarms &&
      vehicleOnlinePanelData &&
      userId &&
      alarmsForUser[0] &&
      alarmsForUser[0].users.includes(userId)
    ) {
      return true;
    }
    return false;
  };

  const checkEventOnVehicle = (): boolean => {
    const eventForVehicle = vehicle.onlinePanel;
    // const userId = getUserId();

    let eventsForUser: IAnnouncement[] = [];

    if (eventForVehicle && eventForVehicle.announcements) {
      if (eventsSettings && doors && additional && bluecoins) {
        eventsForUser = eventForVehicle.announcements.filter(
          (vehicleEvent: IAnnouncement) => vehicleEvent.type === ANNOUNCEMENT_TYPE_EVENT
        );
      } else if (eventsSettings && doors && !additional && !bluecoins) {
        eventsForUser = eventForVehicle.announcements.filter(
          (vehicleEvent: IAnnouncement) =>
            vehicleEvent.type === ANNOUNCEMENT_TYPE_EVENT &&
            vehicleEvent.eventType === ANNOUNCEMENT_TYPE_EVENT_DOORS
        );
      } else if (eventsSettings && additional && !doors && !bluecoins) {
        eventsForUser = eventForVehicle.announcements.filter(
          (vehicleEvent: IAnnouncement) =>
            vehicleEvent.type === ANNOUNCEMENT_TYPE_EVENT &&
            vehicleEvent.eventType === ANNOUNCEMENT_TYPE_EVENT_BINARY
        );
      } else if (eventsSettings && bluecoins && !doors && !additional) {
        eventsForUser = eventForVehicle.announcements.filter(
          (vehicleEvent: IAnnouncement) =>
            vehicleEvent.type === ANNOUNCEMENT_TYPE_EVENT &&
            vehicleEvent.eventType === ANNOUNCEMENT_TYPE_EVENT_BLUECOINS
        );
      }
    }
    if (
      eventsSettings &&
      (doors || additional || bluecoins) &&
      vehicle.onlinePanel &&
      eventsForUser.length > 0
    ) {
      return true;
    }
    return false;
  };

  const hasAlarm = (): boolean => {
    if (
      vehicle &&
      (vehicle.drivingMode === 'business' ||
        (vehicle.drivingMode === 'private' && isVehicleGdprDataAllowed[vehicle.id])) &&
      checkAlarmOnVehicle()
    ) {
      return true;
    }
    return false;
  };

  const isPrivate = (): boolean => {
    if (vehicle.drivingMode === 'private' && isVehicleGdprDataAllowed[vehicle.id]) {
      return true;
    }
    return false;
  };

  const isEvent = (): boolean => {
    if (
      vehicle &&
      checkEventOnVehicle() &&
      (vehicle.drivingMode === 'business' ||
        (vehicle.drivingMode === 'private' && isVehicleGdprDataAllowed[vehicle.id]))
    ) {
      return true;
    }
    return false;
  };
  const isEventTypeDoor = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.length > 0 &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          item.type === ANNOUNCEMENT_TYPE_EVENT && item.eventType === ANNOUNCEMENT_TYPE_EVENT_DOORS
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventTypeAdditional = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          item.type === ANNOUNCEMENT_TYPE_EVENT && item.eventType === ANNOUNCEMENT_TYPE_EVENT_BINARY
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventTypeBluecoins = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          item.type === ANNOUNCEMENT_TYPE_EVENT &&
          item.eventType === ANNOUNCEMENT_TYPE_EVENT_BLUECOINS
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventDoorsBluecoins = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          (item.type === ANNOUNCEMENT_TYPE_EVENT &&
            item.eventType === ANNOUNCEMENT_TYPE_EVENT_BLUECOINS) ||
          item.eventType === ANNOUNCEMENT_TYPE_EVENT_DOORS
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventDoorsAdditional = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          (item.type === ANNOUNCEMENT_TYPE_EVENT &&
            item.eventType === ANNOUNCEMENT_TYPE_EVENT_BINARY) ||
          item.eventType === ANNOUNCEMENT_TYPE_EVENT_DOORS
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventAdditionalBluecoins = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          (item.type === ANNOUNCEMENT_TYPE_EVENT &&
            item.eventType === ANNOUNCEMENT_TYPE_EVENT_BINARY) ||
          item.eventType === ANNOUNCEMENT_TYPE_EVENT_BLUECOINS
      ).length
    ) {
      return true;
    }
    return false;
  };
  const isEventTypeDoorsBluecoins = (): boolean => {
    if (
      vehicle &&
      vehicle.onlinePanel &&
      vehicle.onlinePanel.announcements.filter(
        (item: IAnnouncement) =>
          (item.type === ANNOUNCEMENT_TYPE_EVENT &&
            item.eventType === ANNOUNCEMENT_TYPE_EVENT_DOORS) ||
          item.eventType === ANNOUNCEMENT_TYPE_EVENT_BLUECOINS
      ).length
    ) {
      return true;
    }
    return false;
  };

  // When ride player is open - do not render vehicle marker
  if (isRidePlayerOpen) {
    return null;
  }

  const showVehicleName = () => {
    return identification && vehicleName && vehicle.name;
  };

  const showDriverName = () => {
    return identification && driverNameSettings && vehicle.driver.name;
  };

  const showLicenceNumber = () => {
    return identification && licenceNumberSettings && vehicle.licenceNumber;
  };

  const showAlarm = () => {
    if (alarms) {
      return hasAlarm();
    }
    return false;
  };

  const showEvent = () => {
    if (eventsSettings) {
      if (doors && additional && bluecoins) {
        return isEvent();
      }
      if (doors && !additional && bluecoins) {
        return isEventDoorsBluecoins();
      }
      if (doors && additional && !bluecoins) {
        return isEventDoorsAdditional();
      }
      if (!doors && additional && bluecoins) {
        return isEventAdditionalBluecoins();
      }
      if (doors && !additional && !bluecoins) {
        return isEventTypeDoor();
      }
      if (!doors && additional && !bluecoins) {
        return isEventTypeAdditional();
      }
      if (!doors && !additional && bluecoins) {
        return isEventTypeBluecoins();
      }
      if (doors && !additional && bluecoins) {
        return isEventTypeDoorsBluecoins();
      }
    }
    return false;
  };

  const isWithIdentification = () => {
    return showLicenceNumber() || showVehicleName() || showDriverName();
  };

  // const showIcons = (isChipsArea?: boolean) => {
  //   if ((isChipsArea && isWithIdentification()) || (!isChipsArea && !isWithIdentification())) {
  //     return (
  //       <div>
  //         {isPrivate() && <img src={privateIconMap} alt="private-icon" />}
  //         {showAlarm() && <img src={alarmIconMap} alt="alarm-icon" />}
  //         {showEvent() && <img src={eventIcon} alt="event-icon" />}
  //       </div>
  //     );
  //   }
  //   return false;
  // };

  const showIcons = (): JSX.Element | null => {
    if (isWithIdentification() || !isWithIdentification()) {
      return (
        <div className="vehicle-marker-event-icons">
          {isPrivate() && (
            <img src={IconService.getIsPrivateIcon(vehicle.status)} alt="private-icon" />
          )}
          {showAlarm() && (
            <img src={new URL('/assets/map-alarm.svg', import.meta.url).href} alt="alarm-icon" />
          )}
          {showEvent() && (
            <img src={new URL('/assets/events.svg', import.meta.url).href} alt="event-icon" />
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="vehicle-marker"
      id={`vehicle-marker-${vehicle.id}`}
      style={{
        opacity: getOpacity(),
        zIndex: props.zIndex,
      }} // fix position on map
    >
      <div style={{ position: 'relative' }}>
        {/* <div
          style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translate(-50%)' }}
        >
          {renderIcons()}
        </div> */}
        <div
          className="e-map-pin-info fixed-width"
          style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translate(-50%)' }}
        >
          {showIcons()}
          {showLicenceNumber() && (
            <div>
              <span className="info">{vehicle.licenceNumber}</span>
            </div>
          )}
          {showVehicleName() && (
            <div>
              <span className="info">{vehicle.name}</span>
            </div>
          )}
          {showDriverName() && (
            <div>
              <span className="info">{vehicle.driver.name}</span>
            </div>
          )}
        </div>
      </div>
      <div onClick={handleOnClick} style={{ cursor: getOpacity() === 1 ? 'pointer' : 'revert' }}>
        <SvgIcon
          vehicle={vehicle}
          isSelected={isOnlinePanelOpen === vehicle.id}
          forceVehicleTypeId={vehicle.id === Config.mucha ? 999 : undefined}
        />
      </div>
    </div>
  );
}
